/**
 * Functional-block canvas — the schematic page's "Blocks" view.
 *
 * Pipeline mirrors the Power Tool canvas:
 *   1. `buildBlocksElkGraph(payload)` — block list + thresholded
 *      inter-block edges → ELK input.
 *   2. ELK runs `layered` layout (RIGHT-flow, ORTHOGONAL routing).
 *   3. ELK output → react-flow `Node[]` / `Edge[]` with our
 *      `BlockNode` and the shared `OrthogonalEdge` from `@lib/elk`.
 *
 * Edge stroke width scales with `net_count` so heavier interfaces
 * (e.g. an 8-line memory bus between MCU and FRAM) draw bolder
 * than a 2-line interrupt path. Single-net edges never reach the
 * canvas — backend thresholds at >=2 to keep visual noise down.
 */
import {
  Background,
  Controls,
  type Edge,
  type EdgeTypes,
  MarkerType,
  type Node,
  type NodeTypes,
  ReactFlow,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";

import { Box, CircularProgress, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import ELK from "elkjs/lib/elk.bundled.js";
import { useEffect, useMemo, useState } from "react";

import { OrthogonalEdge } from "@lib/elk/OrthogonalEdge";
import type { ElkEdgeSection } from "@lib/elk/useElkLayout";
import type { BlockEntry, BlocksPayload } from "@models/circuit-ir";

import { BlockNode, type BlockNodeData } from "./BlockNode";

const ElkCtor = (ELK as unknown as { default?: typeof ELK }).default ?? ELK;
const elk = new ElkCtor();

const BLOCK_NODE_WIDTH = 220;
const BLOCK_NODE_HEIGHT = 96;

interface BlocksCanvasProps {
  payload: BlocksPayload;
  /** Click handler — opens the block drawer. */
  onBlockClick?: (block: BlockEntry) => void;
}

interface BlocksElkNode {
  id: string;
  width: number;
  height: number;
  x?: number;
  y?: number;
}

interface BlocksElkEdge {
  id: string;
  sources: string[];
  targets: string[];
  netCount: number;
  sections?: ElkEdgeSection[];
}

interface BlocksElkGraph {
  id: string;
  layoutOptions: Record<string, string>;
  children: BlocksElkNode[];
  edges: BlocksElkEdge[];
}

interface CanvasState {
  layouted: BlocksElkGraph | null;
  error: string | null;
  isLoading: boolean;
}

export function BlocksCanvas({ payload, onBlockClick }: BlocksCanvasProps) {
  const theme = useTheme();

  const elkInput = useMemo(() => buildBlocksElkGraph(payload), [payload]);

  const [state, setState] = useState<CanvasState>({
    layouted: null,
    error: null,
    isLoading: true,
  });

  useEffect(() => {
    let cancelled = false;
    setState((s) => ({ ...s, isLoading: true, error: null }));
    (async () => {
      try {
        const out = await elk.layout(elkInput as never);
        if (cancelled) return;
        setState({
          layouted: out as BlocksElkGraph,
          error: null,
          isLoading: false,
        });
      } catch (err) {
        if (cancelled) return;
        setState({
          layouted: null,
          error: err instanceof Error ? err.message : String(err),
          isLoading: false,
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [elkInput]);

  const { nodes, edges } = useMemo(
    () =>
      toReactFlow(
        state.layouted,
        elkInput,
        payload,
        theme.palette.text.secondary,
      ),
    [state.layouted, elkInput, payload, theme.palette.text.secondary],
  );

  if (state.isLoading && !state.layouted) {
    return (
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
        }}
      >
        <CircularProgress size={18} />
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          Laying out {payload.blocks.length} blocks…
        </Typography>
      </Box>
    );
  }

  if (state.error) {
    return (
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "error.main",
        }}
      >
        <Typography variant="body2">Layout failed: {state.error}</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={(t) => ({
        flex: 1,
        minHeight: 0,
        position: "relative",
        "& .react-flow": { background: "transparent" },
        "& .react-flow__controls": {
          background: t.palette.surface.raised,
          border: `1px solid ${t.palette.border.default}`,
          boxShadow: "none",
        },
        "& .react-flow__controls button": {
          background: "transparent",
          color: t.palette.text.secondary,
          borderColor: t.palette.border.default,
          "&:hover": { color: t.palette.primary.main },
        },
      })}
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        fitView
        proOptions={{ hideAttribution: true }}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable
        onNodeClick={(_, node) => {
          if (!onBlockClick) return;
          const data = node.data as unknown as BlockNodeData;
          if (data.block) onBlockClick(data.block);
        }}
      >
        <Background gap={16} size={1} color={theme.palette.border.default} />
        <Controls showInteractive={false} />
      </ReactFlow>
    </Box>
  );
}

const nodeTypes: NodeTypes = { block: BlockNode };
const edgeTypes: EdgeTypes = { orthogonal: OrthogonalEdge };

function buildBlocksElkGraph(payload: BlocksPayload): BlocksElkGraph {
  const children: BlocksElkNode[] = payload.blocks.map((b) => ({
    id: b.id,
    width: BLOCK_NODE_WIDTH,
    height: BLOCK_NODE_HEIGHT,
  }));

  const edges: BlocksElkEdge[] = payload.edges.map((e) => ({
    id: `e-${e.source_block_id}-${e.target_block_id}`,
    sources: [e.source_block_id],
    targets: [e.target_block_id],
    netCount: e.net_count,
  }));

  return {
    id: "blocks",
    layoutOptions: {
      "elk.algorithm": "layered",
      "elk.direction": "RIGHT",
      // Same orthogonal-routing setup as the power tree — ELK
      // computes proper bend points; OrthogonalEdge consumes them.
      "elk.edgeRouting": "ORTHOGONAL",
      "elk.portConstraints": "FIXED_SIDE",
      "elk.layered.unnecessaryBendpoints": "true",
      "elk.spacing.nodeNode": "36",
      "elk.spacing.edgeNode": "24",
      "elk.spacing.edgeEdge": "16",
      "elk.layered.spacing.nodeNodeBetweenLayers": "120",
      "elk.layered.spacing.edgeNodeBetweenLayers": "48",
      "elk.layered.spacing.edgeEdgeBetweenLayers": "20",
      "elk.padding": "[top=24,left=24,bottom=24,right=24]",
      "elk.layered.crossingMinimization.strategy": "LAYER_SWEEP",
      "elk.layered.nodePlacement.strategy": "BRANDES_KOEPF",
    },
    children,
    edges,
  };
}

function toReactFlow(
  layouted: BlocksElkGraph | null,
  input: BlocksElkGraph,
  payload: BlocksPayload,
  edgeStroke: string,
): { nodes: Node[]; edges: Edge[] } {
  if (!layouted?.children) return { nodes: [], edges: [] };

  const blockById = new Map(payload.blocks.map((b) => [b.id, b]));
  const layoutedNodeById = new Map(
    (layouted.children ?? []).map((c) => [c.id, c]),
  );
  const inputEdgeById = new Map(input.edges.map((e) => [e.id, e]));
  const edgePayloadById = new Map(
    payload.edges.map((e) => [
      `e-${e.source_block_id}-${e.target_block_id}`,
      e,
    ]),
  );

  const nodes: Node[] = [];
  for (const inputNode of input.children) {
    const layoutedNode = layoutedNodeById.get(inputNode.id);
    const block = blockById.get(inputNode.id);
    if (!block) continue;
    nodes.push({
      id: inputNode.id,
      type: "block",
      position: {
        x: layoutedNode?.x ?? 0,
        y: layoutedNode?.y ?? 0,
      },
      data: { block, highlight: false } satisfies BlockNodeData,
      width: inputNode.width,
      height: inputNode.height,
      style: { background: "transparent", border: "none", padding: 0 },
    });
  }

  // Compute the max net_count once so edge stroke widths normalise
  // against the heaviest interface on this board (instead of an
  // absolute scale that would wash out smaller boards).
  const maxNetCount = Math.max(1, ...payload.edges.map((e) => e.net_count));

  const edges: Edge[] = [];
  for (const e of layouted.edges ?? []) {
    const inputEdge = inputEdgeById.get(e.id);
    const edgePayload = edgePayloadById.get(e.id);
    const netCount = edgePayload?.net_count ?? inputEdge?.netCount ?? 1;
    const strokeWidth = scaleStrokeWidth(netCount, maxNetCount);
    edges.push({
      id: e.id,
      source: e.sources[0],
      target: e.targets[0],
      type: "orthogonal",
      data: { sections: e.sections },
      style: {
        stroke: edgeStroke,
        strokeWidth,
        opacity: 0.85,
      },
      markerEnd: {
        type: MarkerType.ArrowClosed,
        color: edgeStroke,
        width: 12,
        height: 12,
      },
    });
  }

  return { nodes, edges };
}

/** Scale stroke width by net_count, normalised against the
 *  board's heaviest interface. Sqrt curve keeps differences
 *  visible at the low end (3 vs 5 nets) without letting an
 *  outlier (50-net memory bus) dominate. Range: 1.0 .. 3.0 px. */
function scaleStrokeWidth(netCount: number, maxNetCount: number): number {
  if (maxNetCount <= 1) return 1.25;
  const ratio = Math.sqrt(netCount / maxNetCount);
  return 1 + 2 * ratio;
}

// Keep `alpha` available for theme overrides — referenced via the
// `BLOCK_NODE_*` constants when the canvas needs to introspect
// theme tokens. (Re-exported through the parent component's
// `theme.palette`; included here for parity with Circuit/Power
// canvases that rely on the same import.)
void alpha;
